import type { PropostaItem, SupportPricingTier } from '@/types'
import { findCatalogPlanForItem, type CatalogPlan } from '@/utils/planCatalog'
import { isSuporteEvolucaoPlan } from '@/utils/suporteEvolucaoSlideLayout'
import { formatCurrency } from '@/utils/format-currency'

export type { SupportPricingTier }

export const DEFAULT_SUPORTE_HOURLY_RATE = 170

export function createSupportTierId(): string {
  return `tier-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function normalizeSupportTiers(
  tiers: SupportPricingTier[] | undefined,
): SupportPricingTier[] {
  if (!Array.isArray(tiers)) return []
  return tiers
    .filter(
      (t) =>
        t &&
        Number(t.supportHours) > 0 &&
        Number(t.monthlyValue) > 0 &&
        typeof t.id === 'string',
    )
    .map((t) => ({
      id: t.id,
      supportHours: Number(t.supportHours),
      monthlyValue: Number(t.monthlyValue),
    }))
    .sort((a, b) => a.supportHours - b.supportHours)
}

export function hasMultipleSupportTiers(tiers: SupportPricingTier[] | undefined): boolean {
  return normalizeSupportTiers(tiers).length >= 2
}

/** Linha do slide/PPTX para uma faixa (ex.: proposta multi-opção). */
export function formatSupportTierSlideLine(hours: number, monthlyValue: number): string {
  const h = Number(hours)
  const value = Number(monthlyValue)
  if (!Number.isFinite(h) || h <= 0 || !Number.isFinite(value) || value <= 0) return ''
  const hoursLabel = Number.isInteger(h) ? `${h}` : String(h).replace('.', ',')
  return `${hoursLabel} horas de Suporte e Evolução por mês — Valor mensal: ${formatCurrency(value)}`
}

export function resolveSupportTiersFromPlan(plan: PropostaItem): SupportPricingTier[] {
  return normalizeSupportTiers(plan.pricing?.supportTiers)
}

/** Sincroniza horas/valor principal a partir da primeira faixa (menor volume de horas). */
export function primarySupportTier(tiers: SupportPricingTier[]): SupportPricingTier | undefined {
  const normalized = normalizeSupportTiers(tiers)
  return normalized[0]
}

/** Linhas para slide/PPTX quando há múltiplas faixas de horas/preço. */
export function resolveSupportTierSlideLines(
  plan: PropostaItem,
  _catalogPlans?: CatalogPlan[],
): string[] {
  const tiers = resolveSupportTiersFromPlan(plan)
  if (tiers.length < 2) return []
  return tiers
    .map((t) => formatSupportTierSlideLine(t.supportHours, t.monthlyValue))
    .filter(Boolean)
}

export function calculateSuporteMonthlyValue(hours: number, hourlyRate: number): number {
  if (!Number.isFinite(hours) || !Number.isFinite(hourlyRate) || hours <= 0 || hourlyRate <= 0) {
    return 0
  }
  return Math.round(hours * hourlyRate * 100) / 100
}

export function inferSuporteHoursFromPricing(
  installmentValue: number,
  hourlyRate: number,
): number {
  if (!hourlyRate || hourlyRate <= 0 || !installmentValue) return 0
  return Math.round((installmentValue / hourlyRate) * 100) / 100
}

/** Texto do informativo de horas no slide (bloco verde, abaixo da descrição). */
export function formatSupportHoursSlideLabel(hours: number): string {
  const value = Number(hours)
  if (!Number.isFinite(value) || value <= 0) return ''
  const hoursText = Number.isInteger(value)
    ? `${value}h`
    : `${String(value).replace('.', ',')}h`
  return `${hoursText}/mês de Suporte e Evolução.`
}

function catalogSignalsSuporte(master?: CatalogPlan): boolean {
  if (!master) return false
  if (master.category?.toLowerCase() === 'suporte') return true
  const projectType = String(master.config?.projectType ?? '').toLowerCase()
  return projectType.includes('suporte')
}

/** Planos de suporte (ex.: Suporte e Evolução) com precificação por hora. */
export function isSuportePlan(
  plan: PropostaItem,
  catalogPlans?: CatalogPlan[],
): boolean {
  if (isSuporteEvolucaoPlan(plan)) return true
  if (plan.projectType?.toLowerCase().includes('suporte')) return true

  if (catalogPlans?.length) {
    const master = findCatalogPlanForItem(plan, catalogPlans)
    if (catalogSignalsSuporte(master)) return true
  }

  return false
}

export function resolveSuportePricingDefaults(
  plan: PropostaItem,
  catalogPlans?: CatalogPlan[],
): { supportHours: number; hourlyRate: number } {
  const master = catalogPlans?.length ? findCatalogPlanForItem(plan, catalogPlans) : undefined
  const cfg = (master?.config ?? {}) as unknown as Record<string, unknown>
  const hourlyRate = Number(
    plan.pricing?.hourlyRate ?? cfg.hourlyRate ?? DEFAULT_SUPORTE_HOURLY_RATE,
  )
  const installmentValue = Number(
    plan.pricing?.installmentValue ?? plan.valorUnitario ?? master?.valor ?? 0,
  )
  const supportHours = Number(
    plan.pricing?.supportHours ??
      cfg.totalHours ??
      inferSuporteHoursFromPricing(installmentValue, hourlyRate) ??
      0,
  )

  return {
    supportHours: supportHours > 0 ? supportHours : 10,
    hourlyRate: hourlyRate > 0 ? hourlyRate : DEFAULT_SUPORTE_HOURLY_RATE,
  }
}

/** Horas para exibir no slide/PPTX (item salvo ou catálogo). */
export function resolveSupportHoursForSlide(
  plan: PropostaItem,
  catalogPlans?: CatalogPlan[],
): number {
  if (!isSuportePlan(plan, catalogPlans)) return 0
  return resolveSuportePricingDefaults(plan, catalogPlans).supportHours
}

export function mergeSuportePricingIntoItem(
  item: PropostaItem,
  catalogPlans?: CatalogPlan[],
): PropostaItem {
  if (!isSuportePlan(item, catalogPlans) || !item.pricing) return item
  const defaults = resolveSuportePricingDefaults(item, catalogPlans)
  const tiers = normalizeSupportTiers(item.pricing.supportTiers)
  const primary = primarySupportTier(tiers)
  return {
    ...item,
    pricing: {
      ...item.pricing,
      supportHours: Number(item.pricing.supportHours) > 0 ? item.pricing.supportHours : defaults.supportHours,
      hourlyRate: Number(item.pricing.hourlyRate) > 0 ? item.pricing.hourlyRate : defaults.hourlyRate,
      ...(tiers.length >= 2 ? { supportTiers: tiers } : {}),
      ...(primary && tiers.length >= 2
        ? {
            supportHours: primary.supportHours,
            installmentValue: primary.monthlyValue,
          }
        : {}),
    },
  }
}
